import { isDefined } from "@clipboard-health/util-ts";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";
import constate from "constate";
import { isEqual } from "lodash";
import { useEffect, useState } from "react";

import { useWorkerPreferences } from "../../../Agents/api/useWorkerPreferences";
import { DEFAULT_DISTANCE_IN_MILES_FILTER } from "./constants";
import { type ShiftTimeSlotFilterOption, type UserFilters } from "./types";
import { useStoredUserFilters } from "./useStoredUserFilters";

interface UserFiltersContext extends UserFilters {
  appliedFiltersCount: number;
  clearDates: () => void;
  clearFilters: () => void;
  setDates: (dates: string[]) => void;
  setDistance: (value: number) => void;
  setFilters: (filters: Partial<UserFilters>) => void;
  setLicenses: (value?: string[]) => void;
  setShiftTimeSlots: (value?: ShiftTimeSlotFilterOption[]) => void;
  availableLicenses: string[];
}

function parseAvailableLicenses(selectedLicenses: string[], availableLicenses: string[]) {
  const availableLicensesSet = new Set(availableLicenses);
  return selectedLicenses.filter((license) => availableLicensesSet.has(license));
}

function useShiftDiscoveryUserFilters(): UserFiltersContext {
  const [dates, setDates] = useState<string[]>([]);

  const clearDates = () => {
    setDates([]);
  };

  const { filters: agentPreferences, setFilters: setAgentPreferences } = useWorkerPreferences();
  const worker = useDefinedWorker();

  const { distance = DEFAULT_DISTANCE_IN_MILES_FILTER } = agentPreferences;

  const setDistance = (value: number) => {
    setAgentPreferences({ ...agentPreferences, distance: value });
  };

  // Dates don't need to be tracked in local storage as they change frequently.
  // Distance also doesn't need to be tracked in local storage because
  // it's already in the agent preferences.
  const {
    storedFilters,
    modifiedStoredFiltersCount,
    setStoredFilters,
    setStoredFilter,
    clearStoredFilters,
  } = useStoredUserFilters();

  const [shiftTimeSlots, setShiftTimeSlots] = useState<ShiftTimeSlotFilterOption[]>([]);

  const clearShiftTimeSlots = () => {
    setShiftTimeSlots([]);
  };

  const updateShiftTimeSlots = (values?: ShiftTimeSlotFilterOption[]) => {
    setShiftTimeSlots(values ?? []);
  };

  const { data: workerQualifications, isSuccess: workerQualificationsAreLoaded } =
    useWorkerQualifications();

  // Make sure stored licenses are still available
  useEffect(() => {
    if (!workerQualificationsAreLoaded) {
      return;
    }

    const availableLicenses = workerQualifications?.map((q) => q.attributes.name) ?? [];
    const parsedLicenses = parseAvailableLicenses(storedFilters.licenses ?? [], availableLicenses);

    if (!isEqual(parsedLicenses, storedFilters.licenses)) {
      setStoredFilter("licenses", parsedLicenses);
    }
  }, [worker, storedFilters, setStoredFilter, workerQualificationsAreLoaded, workerQualifications]);

  const setLicenses = (values?: string[]) => {
    setStoredFilter("licenses", values ?? []);
  };

  const setIncludeWindowedOutShifts = (value: boolean) => {
    setStoredFilter("includeWindowedOutShifts", value);
  };

  const setFilters = (filters: Partial<UserFilters>) => {
    const {
      dates,
      distance: newDistance,
      shiftTimeSlots: newShiftTimeSlots,
      includeWindowedOutShifts,
      ...rest
    } = filters;

    if (isDefined(dates)) {
      setDates(dates);
    }

    if (isDefined(newDistance)) {
      setDistance(newDistance);
    }

    if (isDefined(newShiftTimeSlots)) {
      setShiftTimeSlots(newShiftTimeSlots);
    }

    if (isDefined(includeWindowedOutShifts)) {
      setIncludeWindowedOutShifts(includeWindowedOutShifts);
    }

    if (Object.keys(rest).length > 0) {
      setStoredFilters((storedFilters) => ({ ...storedFilters, ...rest }));
    }
  };

  const isDistanceFilterSet = distance !== DEFAULT_DISTANCE_IN_MILES_FILTER;
  const isShiftTimeSlotsFilterSet = shiftTimeSlots.length > 0;

  const modifiedUnstoredFiltersCount =
    (isDistanceFilterSet ? 1 : 0) + (isShiftTimeSlotsFilterSet ? 1 : 0);

  const clearFilters = () => {
    clearDates();
    setDistance(DEFAULT_DISTANCE_IN_MILES_FILTER);
    clearStoredFilters();
    clearShiftTimeSlots();
  };

  return {
    appliedFiltersCount: modifiedStoredFiltersCount + modifiedUnstoredFiltersCount,
    clearFilters,
    setFilters,
    /**
     * Date strings in the format of "yyyy-MM-dd"
     */
    dates,
    setDates,
    clearDates,
    distance,
    setDistance,
    setLicenses,
    availableLicenses: workerQualificationsAreLoaded
      ? (workerQualifications?.map((q) => q.attributes.name) ?? [])
      : [],
    ...storedFilters,
    shiftTimeSlots,
    setShiftTimeSlots: updateShiftTimeSlots,
  };
}

export const [ShiftDiscoveryUserFiltersProvider, useShiftDiscoveryUserFiltersContext] = constate(
  useShiftDiscoveryUserFilters
);
